import saveAs from 'file-saver';
import { combineEpics, ofType } from 'redux-observable';
import { switchMap, catchError, mergeMap, map } from 'rxjs/operators';
import { Subject, merge, concat } from 'rxjs';
import { denormalize, normalize } from 'normalizr';
import api from 'api/index';
import { closeAllModals, closeModal } from 'modal/index';
import { history } from 'index';
import { MODAL_CLOSE } from 'modal/store/actionTypes';
import { handleError } from 'base/errors/store/actions';
import { getActiveSpaceId } from 'spaces/store/selectors';
import * as actionTypes from './actionTypes';
import { DELETE_SUCCESS as FOLDER_DELETE_SUCCESS } from '../folders/actionTypes';
import { documentsSchema, documentSchema, tagsSchema } from '../../../store/schema';
import { entitiesFetchSuccess } from '../../../store/entities/actions';
import {
  downloadDocumentSuccess,
  fetchDocumentsByActiveFolderSuccess,
  updateDocumentSuccess,
  deleteDocumentSuccess,
  downloadMultipleDocumentsAndFoldersSuccess,
  fetchDocumentsRecursiveByFolderPathSuccess,
  uploadDocumentsSuccess,
  shareDocumentsSuccess,
  fetchDocumentsByActiveFolder,
  setUploadPercentage,
  updateDocumentPasswordMismatch,
  cloneDocumentSuccess,
  fetchDocumentSuccess,
  fetchDocumentError,
} from './actions';
import { getEntities } from '../../../store/entities/selectors';
import { getActiveFolderId } from '../folders/selectors';
import { fetchFolderDocumentCountBySpaceId, fetchChildren, fetchFolderTree } from '../folders/actions';

const fetchDocumentsByActiveFolderEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_BY_ACTIVE_FOLDER),
    switchMap(action =>
      api.documents.fetchAllByActiveFolder(action.spaceId, action.folderId).pipe(
        mergeMap(response => {
          const { entities, result } = normalize(response.body, documentsSchema);
          return [entitiesFetchSuccess(entities), fetchDocumentsByActiveFolderSuccess(result)];
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const fetchDocumentsRecursiveByFolderPathEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_RECURSIVE_BY_FOLDER_PATH),
    switchMap(action =>
      api.documents.fetchAllRecursiveByFolderPath(action.spaceId, action.folderPath).pipe(
        mergeMap(response => {
          const { entities, result } = normalize(response.body, documentsSchema);
          return [entitiesFetchSuccess(entities), fetchDocumentsRecursiveByFolderPathSuccess(result)];
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const fetchDocumentEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH),
    switchMap(action =>
      api.documents.fetch(action.id).pipe(
        map(response => normalize(response.body, documentSchema)),
        mergeMap(({ entities, result }) => [entitiesFetchSuccess(entities), fetchDocumentSuccess(result)]),
        catchError(error => [handleError(error), fetchDocumentError(action.id)]),
      ),
    ),
  );

const uploadDocumentsEpic = (action$, $state) =>
  action$.pipe(
    ofType(actionTypes.UPLOAD),
    mergeMap(action => {
      const progress$ = new Subject();
      const upload$ = api.documents
        .uploadMultiple({
          fileObjects: action.filesFormData.map(file => file.fileObject),
          // remove ',' from name as a workaround for BI-259
          names: action.filesFormData.map(file => file.name.replace(',', '')),
          spaceId: action.space.id,
          folderId: action.folder ? action.folder.id : null,
          subFolders: action.filesFormData.map(file => file.folderPath),
          progressCallback: event => {
            progress$.next(setUploadPercentage(event.percent));
          },
        })
        .pipe(
          map(response => normalize(response.body, documentsSchema)),
          mergeMap(({ entities, result }) => {
            const spaceId = getActiveSpaceId($state.value);
            const folderId = getActiveFolderId($state.value);
            return [
              entitiesFetchSuccess(entities),
              uploadDocumentsSuccess(result),
              fetchDocumentsByActiveFolder(spaceId, folderId),
              fetchChildren(spaceId, folderId),
              fetchFolderTree(spaceId),
              fetchFolderDocumentCountBySpaceId(spaceId),
              closeModal('DOCUMENT_UPLOAD'),
            ];
          }),
          catchError(error => [handleError(error)]),
        );
      return merge(progress$, upload$);
    }),
  );

const cloneDocumentEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.CLONE),
    switchMap(action =>
      api.documents.clone(action.id).pipe(
        map(response => normalize(response.body, documentSchema)),
        mergeMap(({ entities, result }) => [entitiesFetchSuccess(entities), cloneDocumentSuccess(result)]),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const downloadDocumentEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.DOWNLOAD),
    mergeMap(action =>
      api.documents.download(action.document).pipe(
        map(response => {
          saveAs(response.body, action.document.file.filename);
          return downloadDocumentSuccess(response.text, action.document);
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const downloadMultipleDocumentsAndFoldersEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.DOWNLOAD_MULTIPLE),
    mergeMap(action =>
      api.documents
        .downloadMultiple(
          action.documents.map(document => {
            if (document.tags) {
              document.tags = denormalize(document.tags, tagsSchema, getEntities(state$.value)); // eslint-disable-line
            }
            return document;
          }),
          action.folders,
        )
        .pipe(
          map(response => {
            saveAs(response.body, `${action.title}.zip`);
            return downloadMultipleDocumentsAndFoldersSuccess(response.text, action.documents, action.folders);
          }),
          catchError(error => [handleError(error)]),
        ),
    ),
  );

const updateDocumentEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.UPDATE),
    mergeMap(action => {
      const { document } = action;
      if (document.tags) {
        document.tags = denormalize(document.tags, tagsSchema, getEntities(state$.value));
      }
      const activeFolderId = getActiveFolderId(state$.value);
      const doneActions = response => [
        updateDocumentSuccess(response.body, activeFolderId),
        closeModal('DOCUMENT_EDIT'),
      ];

      const $update = api.documents.update(document).pipe(
        mergeMap(response => {
          const { entities } = normalize(response.body, documentSchema);
          return [entitiesFetchSuccess(entities), ...(action.newFile.length > 0 ? [] : doneActions(response))];
        }),
        catchError(error => [handleError(error)]),
      );

      if (action.newFile.length > 0) {
        let $replaceFile = new Subject();
        $replaceFile = api.documents.replaceFile(document, action.newFile[0], action.password).pipe(
          mergeMap(response => {
            const { entities } = normalize(response.body, documentSchema);
            return [entitiesFetchSuccess(entities), ...doneActions(response)];
          }),
          catchError(error =>
            error.status && error.status === 400 ? [updateDocumentPasswordMismatch()] : [handleError(error)],
          ),
        );
        return concat($update, $replaceFile);
      }
      return $update;
    }),
  );

const deleteDocumentEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.DELETE),
    mergeMap(action =>
      api.documents.delete(action.document).pipe(
        mergeMap(() => [deleteDocumentSuccess(action.document)]),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

// Re-fetch document count after deletion or creation
const documentCountChangedEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.DELETE_SUCCESS, actionTypes.UPLOAD_SUCCESS, FOLDER_DELETE_SUCCESS),
    mergeMap(() => {
      const spaceId = getActiveSpaceId(state$.value);
      return [fetchFolderDocumentCountBySpaceId(spaceId)];
    }),
  );

const shareDocumentsEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.SHARE),
    mergeMap(action =>
      api.documents
        .share(
          action.documentIds,
          action.folderIds,
          action.userIds,
          action.roleIds,
          action.mailingListIds,
          action.ccUserIds,
          action.subject,
          action.message,
        )
        .pipe(
          mergeMap(() => [shareDocumentsSuccess()]),
          catchError(error => [handleError(error)]),
        ),
    ),
  );

const gotoDocumentEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.NAVIGATE_TO_DOCUMENT),
    switchMap(action => {
      history.push(action.document.route);
      return [closeAllModals()];
    }),
  );

const closeDocumentViewEpic = (action$, state$) =>
  action$.pipe(
    ofType(MODAL_CLOSE),
    switchMap(action => {
      if (action.name === 'DOCUMENT_VIEW') {
        const spaceId = getActiveSpaceId(state$.value);
        const folderId = getActiveFolderId(state$.value);
        const path = folderId ? `/spaces/${spaceId}/documents/folder/${folderId}` : `/spaces/${spaceId}/documents`;
        history.push(path);
        return [];
      }
      return [];
    }),
  );

export default combineEpics(
  fetchDocumentsByActiveFolderEpic,
  fetchDocumentsRecursiveByFolderPathEpic,
  fetchDocumentEpic,
  uploadDocumentsEpic,
  downloadDocumentEpic,
  downloadMultipleDocumentsAndFoldersEpic,
  updateDocumentEpic,
  cloneDocumentEpic,
  deleteDocumentEpic,
  documentCountChangedEpic,
  shareDocumentsEpic,
  gotoDocumentEpic,
  closeDocumentViewEpic,
);
